import dayjs from 'dayjs';
import { RefObject } from 'react';
import { Slick } from '@/Infrastructure/slick';
import {
  GridApiPro,
  GridColDef,
  GridColDefExtended,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortCellParams,
  GridValidRowModel
} from '@mui/x-data-grid-pro';
import { BrokerCarrier } from '@api/Trinium/Endpoints/Broker/types';
import { ItemLocationHistory } from '@api/Trinium/Endpoints/Consignment/types';
import { DispatchEvent } from '@api/Trinium/Endpoints/DispatchEvent';
import { Document } from '@api/Trinium/Endpoints/Document';
import { StreetTurn } from '@api/Trinium/Endpoints/StreetTurn/types';
import { ExtendedDriver, ExtendedTractor } from '@hooks/useDriverTractors.types';
import { SalesPerson } from '@api/Trinium/Endpoints/Salepeople/types';

/**
 * Props for rendering a cell
 */
export interface CellProps<T> {
  row: T;
  colDef: GridColDef;
}

/**
 * Sign to return for the "empty sorts last" branch of a comparator.
 * MUI negates a comparator's result for descending sorts, which would otherwise flip empties to
 * the top; pre-negating here cancels that out so empties stay at the bottom in both directions.
 * When called outside the grid (e.g. a plain array `.sort`), no cellParams are passed and it
 * behaves as a normal ascending "nulls last".
 */
export const  emptyLastSign = (cellParams?: GridSortCellParams): number => {
  const sortModel = (cellParams?.api as { getSortModel?: () => { field: string; sort?: string | null }[] } | undefined)?.getSortModel?.();
  const direction = sortModel?.find((item) => item.field === cellParams?.field)?.sort;
  return direction === 'desc' ? -1 : 1;
};

/**
 * Default Sorting Comparator to plugin to Dispatch Cells whose Value Getter method returns
 * a simple string that can be used to compare for sorting.
 * @param v1 First Cell Value
 * @param v2 Second Cell Value to compare against
 * @returns Number
 */
export const defaultSortComparator = (v1: unknown, v2: unknown, cellParams1?: GridSortCellParams) => {
  if (typeof v1 === 'string' && typeof v2 === 'string') {
    const v1Cleaned = v1.trim();
    const v2Cleaned = v2.trim();
    /** Empties go last regardless of sort direction */
    if (!v1Cleaned && !v2Cleaned) {
      return 0;
    }
    if (!v1Cleaned) {
      return emptyLastSign(cellParams1);
    }
    if (!v2Cleaned) {
      return -emptyLastSign(cellParams1);
    }

    if (v1Cleaned > v2Cleaned) {
      return 1;
    } else if (v1Cleaned < v2Cleaned) {
      return -1;
    }
  }

  return 0;
};

/**
 * Sort Comparator for columns whose values represent dates.
 * Attempts to parse v1 and v2 as dayjs objects and compares them chronologically.
 * Invalid or empty dates sort last.
 * @param v1 First Cell Value
 * @param v2 Second Cell Value to compare against
 * @returns Number
 */
export const dateSortComparator = (v1: unknown, v2: unknown, cellParams1?: GridSortCellParams) => {
  const d1 = dayjs(v1 as string);
  const d2 = dayjs(v2 as string);

  const d1Valid = d1.isValid() && v1;
  const d2Valid = d2.isValid() && v2;

  /** Empties go last regardless of sort direction */
  if (!d1Valid && !d2Valid) return 0;
  if (!d1Valid) return emptyLastSign(cellParams1);
  if (!d2Valid) return -emptyLastSign(cellParams1);

  if (d1.isBefore(d2)) return -1;
  if (d1.isAfter(d2)) return 1;
  return 0;
};

export interface AssignDriverToolbarFilters {
  unassigned: boolean;
  onRouteWithNoJobs: boolean;
  hazmatEndorsed: boolean;
  filterModel: GridFilterModel;
}

export interface DriverDataGridProps {
  /** Data to populate in the Grid */
  rows: ExtendedDriver[];
  /** Row Select Callback */
  onRowSelect: (driver: ExtendedDriver | null) => void;
  /** Optional parameter when this Grid is used in tandem with selecting a Tractor to control the slick container */
  slickRef?: RefObject<Slick | null>;
  /** Flag indicating to display the current Tractor column. Defaults to false */
  displayCurrentTractor?: boolean;
  /** Filter model initial values */
  defaultFilterModel?: GridFilterModel;
  /** MUI API Grid Ref for calling component to access internal Grid state */
  gridApiRef?: RefObject<GridApiPro | null>;
  /** Flag indicating that the leg requires hazmat certification */
  legRequiresHazmat?: boolean;
  /** When true, any driver / broker can be selected */
  disableRowSelectValidation?: boolean;
  /** Flag indicating that the grid is loading data */
  isLoading?: boolean;
}

export interface TractorDataGridProps {
  rows: ExtendedTractor[];
  onRowSelect: (tractor: ExtendedTractor | null) => void;
  /** Optional Tractor ID to indicate that a row is the default tractor of a Driver */
  defaultTractorId?: string;
  /** Optional parameter when this Grid is used in tandem with selecting a Driver to control the slick container */
  slickRef?: RefObject<Slick | null>;
  /** Flag indicating to display the Current Driver column. Defaults to hidden. */
  displayCurrentDriver?: boolean;
  /** Filter model initial values */
  defaultFilterModel?: GridFilterModel;
  /** Flag indicating that the leg requires hazmat certification */
  legRequiresHazmat?: boolean;
  /** MUI API Grid Ref for calling component to access internal Grid state */
  gridApiRef?: RefObject<GridApiPro | null>;
  /** Flag indicating that the grid is loading data */
  isLoading?: boolean;
}

export interface BrokerCarrierDataGridProps {
  onChange: (newValue: { driver: BrokerCarrier; tractorId: null }) => void;
}

export interface LegEventDataGridProps {
  /** Data to populate in the Grid */
  rows: DispatchEvent[];
  eventType?: string;
}

export interface ItemLocationHistoryDataGridProps {
  /** Data to populate in the Grid */
  rows: ItemLocationHistory[];
  consignmentId: number;
}

export interface DocumentDataGridProps {
  /** Data to populate in the Grid */
  rows?: Document[];
  consignmentId: number;
}

export interface DocumentToolbarFilters {
  applyFilter: boolean;
  filterModel: GridFilterModel;
}

export interface StreetTurnDataGridProps {
  /** Data to populate the Street Turn Grid */
  rows: StreetTurn[];
  onRowSelect: (streetTurn: StreetTurn | null) => void;
}

export const emptyRowSet: GridRowSelectionModel = {
  type: 'include',
  ids: new Set()
};

/** Which rows an editable cell writes to when saved: only the clicked row, or every selected row */
export type EditSaveScope = 'row' | 'selection';

export interface EditableCellRef {
  saveChanges: (scope: EditSaveScope) => void;
}

export const salepeopleColumnDefinition: GridColDef<SalesPerson>[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    hideSortIcons: true,
    sortable: false,
    disableColumnMenu: true,
    disableReorder: true
  }
];

/**
 * Short label a column displays in its header, for the places that list columns by name rather than render them.
 * renderHeader builds the header from JSX, and headerName is a '|||' delimited export string (see ExportButton),
 * so neither is readable on its own.
 */
export function getColumnLabel<T extends GridValidRowModel>(definition: GridColDefExtended<T>): string {
  return definition.headerDisplayName ?? definition.headerName?.split('|||')[0] ?? definition.field;
}

export function createCursorIcon() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0288d1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>`;
  const encoded = btoa(svg);
  return `url('data:image/svg+xml;base64,${encoded}') 0 18, auto`;
}
